<template>
  <div class="chat-page container-fluid">
    <div class="row g-4">
      <div class="col-12 col-lg-3">
        <aside class="chat-sidebar">
          <header class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 class="mb-0">Salas</h5>
              <small class="text-muted">Converse com a Camarilla</small>
            </div>
            <span class="status-indicator" :class="connectionClass"></span>
          </header>

          <section class="mb-4">
            <h6 class="section-title">Sala Pública</h6>
            <ul class="list-unstyled mb-0">
              <li v-for="room in rooms.public" :key="`public-${room.id}`" class="mb-2">
                <button class="room-button" type="button" :class="{ active: activeRoom && activeRoom.id === room.id }"
                  @click="selectRoom({ ...room })">
                  <i class="bi bi-globe me-2"></i>
                  <span class="flex-grow-1 text-start">{{ room.name }}</span>
                  <span v-if="unread[room.id]" class="badge rounded-pill bg-danger">{{ unread[room.id] }}</span>
                </button>
              </li>
            </ul>
          </section>

          <section class="mb-4">
            <div class="d-flex align-items-center justify-content-between">
              <h6 class="section-title mb-0">Salas Particulares</h6>
            </div>
            <p v-if="!rooms.private.length" class="text-muted small mt-2 mb-0">
              Nenhuma sala particular disponível.
              <span v-if="!canCreatePrivate" class="d-block">Usuários premium podem criar salas particulares.</span>
            </p>
            <ul v-else class="list-unstyled mb-0 mt-2">
              <li v-for="room in rooms.private" :key="`private-${room.id}`" class="mb-2">
                <div class="d-flex align-items-center gap-2">
                  <button class="room-button" type="button" :class="{ active: activeRoom && activeRoom.id === room.id }"
                    @click="handleRoomSelection({ ...room })">
                    <i class="bi bi-shield-lock me-2"></i>
                    <div class="flex-grow-1 text-start">
                      <div class="d-flex align-items-center gap-2">
                        <span class="flex-grow-1" :class="{ 'room-owner-name': room.is_owner }">
                          {{ room.name }}
                        </span>
                        <span class="badge bg-dark text-uppercase">{{ room.access_type === 'public' ? 'Pública' :
                          'Privada' }}</span>
                      </div>
                      <small class="text-muted d-block mt-1">
                        {{ room.access_type === 'public' ? 'Entrada liberada' : 'Exige senha' }} ·
                        {{ room.member_count || 0 }} / {{ room.max_users || 5 }} jogadores
                        <span v-if="!room.is_member" class="text-warning ms-1">(Aguardando entrada)</span>
                      </small>
                    </div>
                    <span v-if="unread[room.id]" class="badge rounded-pill bg-danger">{{ unread[room.id] }}</span>
                  </button>
                  <button v-if="room.owner_id === currentUserId" class="btn btn-outline-danger btn-sm flex-shrink-0"
                    :disabled="deletingRoomId === room.id" title="Excluir sala" @click.stop="confirmDeleteRoom(room)">
                    <span v-if="deletingRoomId === room.id" class="spinner-border spinner-border-sm"></span>
                    <span v-else><i class="bi bi-trash"></i></span>
                  </button>
                </div>
              </li>
            </ul>
            <p v-if="privateManagementError" class="text-danger small mt-2 mb-0">{{ privateManagementError }}</p>
            <div class="d-grid mt-3">
              <button class="btn btn-sm btn-outline-light" type="button"
                :disabled="!canCreatePrivate || privateRoomLimitReached"
                @click="canCreatePrivate ? openPrivateModal() : notifyPremiumRequired()">
                <i class="bi bi-plus-lg me-1"></i>
                Criar sala particular
              </button>
              <small v-if="!canCreatePrivate" class="text-muted mt-2">Exclusivo para membros premium.</small>
              <small v-else-if="privateRoomLimitReached" class="text-warning mt-2">Você já criou o limite de 3 salas
                particulares.</small>
              <small v-else class="text-muted mt-2">Você ainda pode criar {{ remainingPrivateRooms }} sala(s)
                particular(es).</small>
            </div>
          </section>

          <section>
            <h6 class="section-title">Mensagens Diretas</h6>
            <div class="direct-start mb-3">
              <label class="form-label small text-uppercase text-muted mb-1">Iniciar conversa</label>
              <div class="input-group input-group-sm">
                <select class="form-select" v-model="directTargetId">
                  <option disabled value="">Selecione um usuário</option>
                  <option v-for="user in availableContacts" :key="`contact-${user.id}`" :value="user.id">
                    {{ user.name }}
                  </option>
                </select>
                <button class="btn btn-outline-light" type="button" @click="startDirectConversation">
                  <i class="bi bi-send"></i>
                </button>
              </div>
              <p v-if="directError" class="text-danger small mt-2 mb-0">{{ directError }}</p>
            </div>
            <div class="direct-start mb-4">
              <label class="form-label small text-uppercase text-muted mb-1">Enviar pedido de amizade</label>
              <div class="input-group input-group-sm">
                <input type="text" class="form-control" v-model="friendRequestIdentifier"
                  placeholder="Email, nome completo ou apelido">
                <button class="btn btn-outline-light" type="button" :disabled="friendRequestBusy"
                  @click="sendChatFriendRequest">
                  <i class="bi bi-person-plus"></i>
                </button>
              </div>
              <p v-if="friendRequestError" class="text-danger small mt-2 mb-0">{{ friendRequestError }}</p>
              <p v-if="friendRequestMessage" class="text-success small mt-2 mb-0">{{ friendRequestMessage }}</p>
            </div>
            <ul class="list-unstyled mb-0">
              <li v-for="room in rooms.direct" :key="`direct-${room.id}`" class="mb-2">
                <button class="room-button" type="button" :class="{ active: activeRoom && activeRoom.id === room.id }"
                  @click="selectRoom({ ...room })">
                  <i class="bi bi-person-lines-fill me-2"></i>
                  <span class="flex-grow-1 text-start">
                    {{ room.target_user ? room.target_user.name : room.name }}
                  </span>
                  <span v-if="unread[room.id]" class="badge rounded-pill bg-danger">{{ unread[room.id] }}</span>
                </button>
              </li>
            </ul>
          </section>
        </aside>
      </div>

      <div class="col-12 col-lg-9">
        <section class="chat-main h-100">
          <header class="chat-main-header">
            <div>
              <h4 class="mb-0" v-if="activeRoom">{{ activeRoomTitle }}</h4>
              <p class="text-muted mb-0 small" v-if="activeRoom">{{ roomSubtitle }}</p>
            </div>
            <div class="text-end">
              <span class="text-muted small">{{ connectionLabel }}</span>
            </div>
          </header>

          <div v-if="loading" class="chat-state">
            <div class="spinner-border text-light" role="status"></div>
            <p class="mt-3">Carregando chat...</p>
          </div>

          <div v-else-if="!activeRoom" class="chat-state">
            <i class="bi bi-chat-dots display-6 mb-3"></i>
            <p class="mb-0">Selecione uma sala para começar a conversar.</p>
          </div>

          <template v-else>
            <div class="chat-messages" ref="messagesContainer">
              <div v-if="loadingMessages" class="chat-state py-5">
                <div class="spinner-border text-light" role="status"></div>
                <p class="mt-3">Carregando mensagens...</p>
              </div>
              <template v-else>
                <article v-for="message in messages" :key="message.id" class="chat-message"
                  :class="{ mine: message.sender.id === currentUserId }">
                  <header class="d-flex justify-content-between align-items-center mb-1">
                    <strong>{{ message.sender.name }}</strong>
                    <small class="text-muted">{{ formatDate(message.createdAt) }}</small>
                  </header>
                  <div class="message-body">
                    <p v-if="normalizeMessageContent(message).text" class="mb-1"
                      v-text="normalizeMessageContent(message).text"></p>
                    <div v-if="normalizeMessageContent(message).diceRoll" class="dice-roll-container">
                      <div class="dice-roll-number" :class="{ 'dice-roll-animate': animatingDice[message.id] }">
                        🎲 D{{ normalizeMessageContent(message).diceSides || 10 }} →
                        <strong>{{ animatedDiceValue[message.id] ?? '...' }}</strong>
                      </div>
                    </div>
                    <div v-if="normalizeMessageContent(message).audioEnabled" class="text-info small mb-1">
                      <i class="bi bi-soundwave me-1"></i>
                      Microfone habilitado para bate-papo por voz.
                    </div>
                    <figure v-if="normalizeMessageContent(message).image" class="mb-0">
                      <img :src="normalizeMessageContent(message).image.data"
                        :alt="normalizeMessageContent(message).image.name" class="img-fluid rounded" loading="lazy" />
                      <figcaption class="text-muted small mt-1">{{ normalizeMessageContent(message).image.name }}
                      </figcaption>
                    </figure>
                    <p v-if="!normalizeMessageContent(message).text && !normalizeMessageContent(message).diceRoll && !normalizeMessageContent(message).image"
                      class="mb-0">
                      {{ message.content }}
                    </p>
                  </div>
                </article>
                <p v-if="!messages.length" class="text-center text-muted my-5">
                  Nenhuma mensagem ainda. Seja o primeiro a iniciar a conversa!
                </p>
              </template>
            </div>

            <footer class="chat-input">
              <p v-if="socketError" class="text-danger small text-center mb-2">{{ socketError }}</p>
              <div class="chat-tools d-flex align-items-center gap-2 mb-2">
                <button type="button" class="btn btn-outline-light btn-sm" :class="{ active: microphoneEnabled }"
                  @click="toggleVoice">
                  <i :class="microphoneEnabled ? 'bi bi-mic-fill' : 'bi bi-mic-mute'" class="me-1"></i>
                  Áudio
                </button>
                <div class="position-relative">
                  <button type="button" class="btn btn-outline-light btn-sm"
                    @click="emojiPaletteVisible = !emojiPaletteVisible">
                    <i class="bi bi-emoji-smile"></i>
                  </button>
                  <div v-if="emojiPaletteVisible" class="emoji-palette">
                    <button type="button" v-for="emoji in emojiChoices" :key="emoji" class="emoji-button"
                      @click="appendEmoji(emoji)">
                      {{ emoji }}
                    </button>
                  </div>
                </div>
                <button type="button" class="btn btn-outline-light btn-sm" @click="triggerImageUpload">
                  <i class="bi bi-image"></i>
                  Imagem
                </button>
                <button type="button" class="btn btn-outline-light btn-sm" :disabled="diceCooldownActive"
                  @click="rollDice">
                  <i class="bi bi-dice-5"></i>
                  D10
                </button>
                <span v-if="diceCooldownActive" class="badge bg-danger ms-2">Aguardando...</span>
                <button type="button" class="btn btn-outline-light btn-sm" :disabled="!microphoneEnabled || !pushToTalk"
                  @mousedown="startTalking" @mouseup="stopTalking" @mouseleave="stopTalking">
                  🔊
                </button>
                <!-- <span v-if="pendingDiceRoll" class="badge bg-secondary">Rolagem: {{ pendingDiceRoll }}</span>-->
                <span v-if="attachedImage" class="badge bg-info text-dark">Imagem pronta</span>
                <input ref="imageInput" type="file" accept="image/*" class="d-none" @change="handleImageSelect" />
              </div>
              <p v-if="attachmentError" class="text-danger small mb-2">{{ attachmentError }}</p>
              <form @submit.prevent="sendMessage">
                <div class="input-group">
                  <textarea class="form-control" rows="2" v-model="messageDraft"
                    :disabled="sendingMessage || !socketReady" placeholder="Escreva sua mensagem..."></textarea>
                  <button class="btn btn-vampire" type="submit" :disabled="sendingMessage || !socketReady">
                    <i class="bi bi-caret-right-fill"></i>
                  </button>
                </div>
              </form>
            </footer>
          </template>
        </section>
      </div>
    </div>

    <button class="user-panel-toggle" type="button" @click="toggleRightPanel">
      <i :class="['me-1', rightPanelOpen ? 'bi bi-people-fill' : 'bi bi-people']"></i>
      Participantes
    </button>

    <transition name="user-panel-slide">
      <aside v-if="rightPanelOpen" class="user-panel">
        <div class="user-panel-header d-flex justify-content-between align-items-start mb-3">
          <div>
            <p class="text-uppercase small text-muted mb-1">Sala atual</p>
            <h5 class="mb-0">{{ activeRoomTitle || 'Participantes' }}</h5>
          </div>
          <button class="btn btn-sm btn-outline-light" type="button" @click="toggleRightPanel">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="mb-4">
          <p class="text-muted small mb-2">Seu status</p>
          <div class="btn-group w-100 status-toggle" role="group">
            <button v-for="option in statusOptions" :key="option.value" type="button" class="btn btn-sm"
              :class="currentStatus === option.value ? 'btn-vampire' : 'btn-outline-light'"
              @click="updateStatus(option.value)">
              <span class="presence-dot me-1" :class="statusDotClass(option.value)"></span>
              {{ option.label }}
            </button>
          </div>
        </div>

        <section class="mb-4">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="section-title mb-0">Usuários online</h6>
            <span class="badge bg-secondary">{{ activePresence.length }}</span>
          </div>
          <p v-if="!activePresence.length" class="text-muted small mb-2">Nenhum Cainita conectado nesta sala.</p>
          <ul v-else class="list-unstyled mb-0 user-list">
            <li v-for="user in activePresence" :key="`presence-${user.id}`" class="user-row">
              <div class="d-flex align-items-center gap-2">
                <span class="presence-dot" :class="statusDotClass(user.status)"></span>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2">
                    <strong>{{ user.name }}</strong>
                    <span v-if="isUserSpeaking(user.id)" class="speaking-indicator" title="Usuário falando">
                      <i class="bi bi-soundwave"></i>
                    </span>
                  </div>
                  <small class="text-muted">{{ user.nickname || '—' }}</small>
                </div>
              </div>
              <span class="badge status-pill" :class="statusDotClass(user.status)">{{ statusLabel(user.status) }}</span>
            </li>
          </ul>
        </section>

        <section>
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h6 class="section-title mb-0">Canal de voz</h6>
            <span class="badge bg-dark">{{ activeRoom ? activeRoom.name : 'Sala' }}</span>
          </div>

          <div class="voice-tools mb-3">
            <div class="form-check form-switch mb-2">
              <input class="form-check-input" type="checkbox" :checked="pushToTalk"
                @change="handlePushToggle($event.target.checked)" id="pushToTalkSwitch">
              <label class="form-check-label" for="pushToTalkSwitch">🎤 Modo push-to-talk</label>
            </div>
            <div class="d-flex flex-wrap gap-2 align-items-center">
              <button type="button" class="btn btn-outline-light btn-sm" :disabled="!microphoneEnabled || !pushToTalk"
                @mousedown="startTalking" @mouseup="stopTalking" @mouseleave="stopTalking">
                🔊 Segurar para falar
              </button>
              <select class="form-select form-select-sm voice-quality-select" :value="voiceQuality"
                @change="applyVoiceQuality($event.target.value)">
                <option value="auto">📶 Qualidade adaptativa</option>
                <option value="alta">Alta</option>
                <option value="media">Média</option>
                <option value="baixa">Baixa</option>
              </select>
            </div>
            <p class="text-muted small mb-0 mt-2">👥 Lista de pessoas conectadas no áudio</p>
          </div>

          <p v-if="!voiceParticipantList.length" class="text-muted small mb-0">Ninguém está no canal de voz desta sala.
          </p>
          <ul v-else class="list-unstyled voice-list mb-0">
            <li v-for="participant in voiceParticipantList" :key="`voice-${participant.id}`" class="voice-row">
              <div class="d-flex align-items-center gap-2 flex-grow-1">
                <span class="presence-dot" :class="statusDotClass(participant.status)"></span>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2">
                    <strong>{{ participant.name }}</strong>
                    <span v-if="isUserSpeaking(participant.id)" class="speaking-indicator" title="Usuário falando">
                      <i class="bi bi-soundwave"></i>
                    </span>
                  </div>
                  <small class="text-muted">{{ statusLabel(participant.status) }}</small>
                </div>
              </div>
              <div class="voice-actions d-flex align-items-center gap-2">
                <button class="btn btn-outline-light btn-sm" type="button" @click="toggleMuteUser(participant.id)">
                  <i :class="mutedUsers[participant.id] ? 'bi bi-volume-mute' : 'bi bi-volume-up'"
                    aria-hidden="true"></i>
                </button>
                <input type="range" min="0" max="100" class="form-range"
                  :value="(voiceVolumes[participant.id] ?? 1) * 100"
                  @input="setUserVolume(participant.id, $event.target.value)" />
              </div>
            </li>
          </ul>
        </section>
      </aside>
    </transition>
    <div v-if="rightPanelOpen" class="user-panel-backdrop" @click="toggleRightPanel"></div>

    <div v-if="policyVisible && policy" class="chat-policy-modal">
      <div class="chat-policy-card">
        <h3 class="mb-3">
          <i class="bi bi-shield-lock me-2"></i>
          {{ policy.title }}
        </h3>
        <p class="text-muted">{{ policy.description }}</p>
        <ul class="mb-4">
          <li v-for="(item, index) in policy.items" :key="`policy-${index}`">{{ item }}</li>
        </ul>
        <button class="btn btn-vampire w-100" type="button" @click="policyVisible = false">
          Li e concordo com as regras
        </button>
      </div>
    </div>

    <div v-if="showPrivateModal" class="chat-modal-backdrop">
      <div class="chat-modal">
        <header class="modal-header justify-content-between">
          <h5 class="modal-title">Criar sala particular</h5>
          <button type="button" class="btn-close" @click="closePrivateModal"></button>
        </header>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Título</label>
            <input type="text" class="form-control" v-model="privateRoomForm.name" required />
            <small class="text-muted">Obrigatório para identificar a sala.</small>
          </div>
          <div class="mb-3">
            <label class="form-label">Tipo</label>
            <select class="form-select" v-model="privateRoomForm.accessType">
              <option value="public">Sala pública</option>
              <option value="private">Sala privada (com senha)</option>
            </select>
            <small class="text-muted">Públicas são visíveis a todos os Cainitas, privadas pedem senha.</small>
          </div>
          <div v-if="privateRoomForm.accessType === 'private'" class="mb-3">
            <label class="form-label">Senha</label>
            <input type="password" class="form-control" v-model="privateRoomForm.password"
              placeholder="Defina uma senha" />
            <small class="text-muted">Obrigatória para proteger o acesso.</small>
          </div>
          <div class="mb-3">
            <label class="form-label">Convidados (opcional)</label>
            <select class="form-select" multiple size="6" v-model="privateRoomForm.participants">
              <option v-for="user in availableContacts" :key="`invite-${user.id}`" :value="user.id">
                {{ user.name }}
              </option>
            </select>
            <small class="text-muted">Máximo de 5 participantes, incluindo você.</small>
          </div>
          <p v-if="privateRoomError" class="text-danger small mb-0">{{ privateRoomError }}</p>
        </div>
        <footer class="modal-footer">
          <button type="button" class="btn btn-outline-light" @click="closePrivateModal">Cancelar</button>
          <button type="button" class="btn btn-vampire" @click="submitPrivateRoom" :disabled="creatingRoom">
            <span v-if="creatingRoom" class="spinner-border spinner-border-sm me-2"></span>
            Criar sala
          </button>
        </footer>
      </div>
    </div>

    <div v-if="showJoinModal && joinTargetRoom" class="chat-modal-backdrop">
      <div class="chat-modal">
        <header class="modal-header justify-content-between">
          <h5 class="modal-title">Entrar em {{ joinTargetRoom.name }}</h5>
          <button type="button" class="btn-close" @click="closeJoinModal"></button>
        </header>
        <div class="modal-body">
          <p class="text-muted">Informe a senha para participar desta sala particular.</p>
          <div class="mb-3">
            <label class="form-label">Senha</label>
            <input type="password" class="form-control" v-model="joinPassword" placeholder="Digite a senha" />
          </div>
          <p v-if="joinError" class="text-danger small mb-0">{{ joinError }}</p>
        </div>
        <footer class="modal-footer">
          <button type="button" class="btn btn-outline-light" @click="closeJoinModal">Cancelar</button>
          <button type="button" class="btn btn-vampire" :disabled="joiningRoom" @click="submitJoinRoom">
            <span v-if="joiningRoom" class="spinner-border spinner-border-sm me-2"></span>
            Entrar
          </button>
        </footer>
      </div>
    </div>
  </div>

  <transition name="modal-fade-zoom">
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="cancelDeleteRoom">
      <div class="modal-dialog">
        <div class="modal-content">

          <div class="modal-header">
            <h5 class="modal-title text-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Confirmar Exclusão
            </h5>
            <button class="btn-close" @click="cancelDeleteRoom"></button>
          </div>

          <div class="modal-body text-center">
            <p class="mb-2">Deseja realmente excluir a sala:</p>

            <strong class="text-danger">
              "{{ roomToDelete?.name }}"
            </strong>

            <p class="mt-3 text-muted small">
              Esta ação não poderá ser desfeita.
            </p>

            <div v-if="privateManagementError" class="alert alert-danger mt-3">
              {{ privateManagementError }}
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cancelDeleteRoom">
              Cancelar
            </button>

            <button class="btn btn-danger" :disabled="deletingRoomId === roomToDelete?.id" @click="deleteRoomConfirmed">
              <span v-if="deletingRoomId === roomToDelete?.id">
                <i class="spinner-border spinner-border-sm me-2"></i>
                Excluindo...
              </span>
              <span v-else>
                <i class="bi bi-trash me-1"></i>
                Excluir
              </span>
            </button>
          </div>

        </div>
      </div>
    </div>
  </transition>

</template>

<script>
import {
  fetchChatContext,
  fetchRoomMessages,
  createPrivateRoom,
  joinCustomRoom,
  openDirectConversation,
  deletePrivateRoom,
  getWebSocketUrl
} from '../services/chat'
import { getStoredToken } from '../services/api'
import { sendFriendRequest } from '../services/friends'

export default {
  name: 'ChatView',
  props: {
    currentUser: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      loadingMessages: false,
      animatedDiceValue: {},
      animatingDice: {},
      diceCooldownActive: false,
      showDeleteModal: false,
      roomToDelete: null,
      rooms: {
        public: [],
        private: [],
        direct: []
      },
      availableContacts: [],
      activeRoom: null,
      messages: [],
      unread: {},
      messageDraft: '',
      socket: null,
      joinedRooms: new Set(),
      socketReady: false,
      socketError: '',
      connectionStatus: 'connecting',
      shouldReconnect: true,
      policy: null,
      policyVisible: true,
      canCreatePrivate: false,
      showPrivateModal: false,
      privateRoomForm: {
        name: '',
        participants: [],
        accessType: 'private',
        password: '',
        maxUsers: 5
      },
      privateRoomError: '',
      privateManagementError: '',
      deletingRoomId: null,
      creatingRoom: false,
      directTargetId: '',
      directError: '',
      friendRequestIdentifier: '',
      friendRequestMessage: '',
      friendRequestError: '',
      friendRequestBusy: false,
      sendingMessage: false,
      showJoinModal: false,
      joinTargetRoom: null,
      joinPassword: '',
      joinError: '',
      joiningRoom: false,
      microphoneEnabled: false,
      voiceStream: null,
      voicePeers: {},
      //pendingDiceRoll: null,
      emojiPaletteVisible: false,
      attachedImage: null,
      attachmentError: '',
      presenceByRoom: {},
      rightPanelOpen: false,
      currentStatus: 'online',
      voiceRosters: {},
      mutedUsers: {},
      voiceVolumes: {},
      speakingUsers: {},
      pushToTalk: false,
      pushToTalkActive: false,
      voiceQuality: 'auto',
      currentVoiceRoomId: null,
      audioAnalysers: {},
      audioElements: {}
    }
  },
  computed: {
    currentUserId() {
      return this.currentUser ? this.currentUser.id : null
    },
    connectionLabel() {
      if (this.connectionStatus === 'connected') {
        return 'Conectado'
      }
      if (this.connectionStatus === 'connecting') {
        return 'Conectando...'
      }
      if (this.connectionStatus === 'reconnecting') {
        return 'Reconectando...'
      }
      return 'Desconectado'
    },
    connectionClass() {
      return {
        connected: this.connectionStatus === 'connected',
        reconnecting: this.connectionStatus === 'reconnecting',
        disconnected: this.connectionStatus === 'disconnected'
      }
    },
    activeRoomTitle() {
      if (!this.activeRoom) {
        return ''
      }

      if (this.activeRoom.room_type === 'direct' && this.activeRoom.target_user) {
        return `Conversa com ${this.activeRoom.target_user.name}`
      }

      return this.activeRoom.name
    },
    roomSubtitle() {
      if (!this.activeRoom) {
        return ''
      }

      if (this.activeRoom.room_type === 'public') {
        return 'Todos os Cainitas podem participar desta sala.'
      }

      if (this.activeRoom.room_type === 'private') {
        return 'Sala privada para convidados selecionados.'
      }

      if (this.activeRoom.room_type === 'direct' && this.activeRoom.target_user) {
        return 'Conversa individual protegida.'
      }

      return ''
    },
    emojiChoices() {
      return ['😀', '😈', '🧛', '🩸', '🎲', '🔥', '🎭', '🫶', '😎']
    },
    activePresence() {
      if (!this.activeRoom) {
        return []
      }
      return this.presenceByRoom[this.activeRoom.id] || []
    },
    voiceParticipantList() {
      if (!this.activeRoom) {
        return []
      }

      const roster = this.voiceRosters[this.activeRoom.id] || []
      const presenceMap = new Map(
        (this.presenceByRoom[this.activeRoom.id] || []).map((user) => [user.id, user])
      )

      return roster.map((participant) => ({
        ...participant,
        status: presenceMap.get(participant.id)?.status || 'online'
      }))
    },
    statusOptions() {
      return [
        { value: 'online', label: 'Online', color: 'success' },
        { value: 'away', label: 'Ausente', color: 'warning' },
        { value: 'busy', label: 'Ocupado', color: 'danger' }
      ]
    },
    ownedPrivateRoomsCount() {
      return this.rooms.private.filter((room) => room.owner_id === this.currentUserId).length
    },
    privateRoomLimitReached() {
      return this.ownedPrivateRoomsCount >= 3
    },
    remainingPrivateRooms() {
      return Math.max(0, 3 - this.ownedPrivateRoomsCount)
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollMessagesToBottom()
        })
      },
      deep: true
    }
  },
  mounted() {
    this.joinedRooms = new Set()
    this.initializeChat()
  },
  beforeUnmount() {
    this.shouldReconnect = false
    if (this.socket) {
      try {
        this.socket.close()
      } catch (error) {
        console.warn("Erro ao fechar socket:", error)
      }
      this.socket = null
    }
  },
  methods: {
    async initializeChat() {
      this.loading = true
      try {
        const context = await fetchChatContext()
        this.rooms = context.rooms
        this.availableContacts = context.availableDirectContacts || []
        this.policy = context.policy
        this.canCreatePrivate = !!context.canCreatePrivate
        this.loading = false

        const defaultRoom = this.rooms.public[0] || this.rooms.private[0] || this.rooms.direct[0]
        if (defaultRoom) {
          this.handleRoomSelection({ ...defaultRoom })
        }

        this.setupWebSocket()
      } catch (error) {
        console.error('Erro ao carregar chat', error)
        this.socketError = error.response?.data?.error || 'Não foi possível carregar o chat.'
        this.loading = false
      }
    },
    setupWebSocket() {
      const token = getStoredToken()
      if (!token) {
        this.socketError = 'Sessão expirada. Faça login novamente.'
        this.connectionStatus = 'disconnected'
        return
      }

      const url = getWebSocketUrl(token)
      try {
        if (this.socket) {
          try {
            this.socket.close()
          } catch (error) {
            console.warn("Erro ao fechar socket:", error)
          }
        }

        this.shouldReconnect = true
        this.connectionStatus = 'connecting'
        this.socket = new WebSocket(url)
        this.socket.addEventListener('open', () => {
          this.connectionStatus = 'connected'
          this.socketReady = true
          this.socketError = ''
          if (this.activeRoom) {
            this.joinRoom(this.activeRoom.id)
          }
        })

        this.socket.addEventListener('close', () => {
          this.socketReady = false
          this.socket = null
          if (this.shouldReconnect) {
            this.connectionStatus = 'reconnecting'
            setTimeout(() => {
              if (this.shouldReconnect) {
                this.setupWebSocket()
              }
            }, 1500)
          } else {
            this.connectionStatus = 'disconnected'
          }
        })

        this.socket.addEventListener('error', () => {
          this.socketError = 'Conexão com o chat foi interrompida.'
          if (!this.shouldReconnect) {
            this.connectionStatus = 'disconnected'
          }
        })

        this.socket.addEventListener('message', (event) => {
          try {
            const payload = JSON.parse(event.data)
            this.handleSocketPayload(payload)
          } catch (error) {
            console.error('Erro ao processar mensagem do chat', error)
          }
        })
      } catch (error) {
        console.error('Falha ao iniciar WebSocket', error)
        this.socketError = 'Não foi possível conectar ao chat em tempo real.'
      }
    },
    async handleSocketPayload(payload) {
      if (!payload || typeof payload !== 'object') {
        return
      }

      if (payload.type === 'connected') {
        if (payload.payload?.defaultRoomId) {
          this.joinRoom(payload.payload.defaultRoomId)
        }
        return
      }

      if (payload.type === 'joined') {
        if (payload.payload?.roomId) {
          this.joinedRooms.add(payload.payload.roomId)
        }
        return
      }

      if (payload.type === 'message') {
        this.handleIncomingMessage(payload.payload)
        return
      }

      if (payload.type === 'presence') {
        this.handlePresence(payload.payload)
        return
      }

      if (payload.type === 'voice-roster') {
        this.handleVoiceRoster(payload.payload)
        return
      }

      if (payload.type === 'voice-users') {
        const { users, roomId } = payload
        if (!this.microphoneEnabled || !this.voiceStream) return

        users.forEach(async (userId) => {
          if (this.voicePeers[userId]) return

          const pc = this.createVoicePeer(userId)
          const offer = await pc.createOffer()
          await pc.setLocalDescription(offer)

          this.socket.send(JSON.stringify({
            type: 'voice-offer',
            roomId,
            targetUserId: userId,
            offer
          }))
        })

        return
      }

      if (payload.type === 'voice-offer') {
        const { fromUserId, offer, roomId } = payload
        if (!this.microphoneEnabled || !this.voiceStream) return

        const pc = this.createVoicePeer(fromUserId)
        await pc.setRemoteDescription(offer)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)

        this.socket.send(JSON.stringify({
          type: 'voice-answer',
          roomId,
          targetUserId: fromUserId,
          answer
        }))

        return
      }

      if (payload.type === 'voice-answer') {
        const { fromUserId, answer } = payload
        const pc = this.voicePeers[fromUserId]
        if (!pc) return

        await pc.setRemoteDescription(answer)
        return
      }

      if (payload.type === 'voice-ice') {
        const { fromUserId, candidate } = payload
        const pc = this.voicePeers[fromUserId]
        if (!pc) return

        await pc.addIceCandidate(candidate)
        return
      }


      if (payload.type === 'error') {
        this.socketError = payload.message || 'Ocorreu um erro no chat.'
      }
    },
    handleIncomingMessage(message) {
      if (!message || !message.roomId) {
        return
      }

      const roomId = message.roomId
      const existingRoom = this.findRoom(roomId)
      if (!existingRoom) {
        const newRoom = {
          id: roomId,
          name: message.roomName || 'Nova conversa',
          room_type: message.roomType || 'direct',
          access_type: message.accessType || 'private',
          is_member: true
        }
        if (message.roomType === 'direct' && message.sender && message.sender.id !== this.currentUserId) {
          newRoom.target_user = {
            id: message.sender.id,
            name: message.sender.name
          }
        }
        if (newRoom.room_type === 'direct') {
          this.rooms.direct.push(newRoom)
        } else if (newRoom.room_type === 'private') {
          this.rooms.private.push(newRoom)
        }
      }

      if (!this.activeRoom || this.activeRoom.id !== roomId) {
        this.unread = {
          ...this.unread,
          [roomId]: (this.unread[roomId] || 0) + 1
        }
        return
      }

      this.messages = [...this.messages, message]

      this.$nextTick(() => {
        this.animateDice(message)
      })
    },
    handlePresence(payload) {
      if (!payload || !payload.roomId) {
        return
      }

      this.presenceByRoom = {
        ...this.presenceByRoom,
        [payload.roomId]: payload.users || []
      }
    },
    handleVoiceRoster(payload) {
      if (!payload || !payload.roomId) {
        return
      }

      this.voiceRosters = {
        ...this.voiceRosters,
        [payload.roomId]: payload.participants || []
      }

      if (payload.roomId === this.currentVoiceRoomId) {
        this.syncVoicePeersWithRoster(payload.roomId)
      }
    },
    async selectRoom(room) {
      if (!room || !room.id) {
        return
      }

      this.activeRoom = room
      this.unread = {
        ...this.unread,
        [room.id]: 0
      }

      if (this.microphoneEnabled && this.currentVoiceRoomId && this.currentVoiceRoomId !== room.id) {
        this.disableVoice()
      }
      this.currentVoiceRoomId = room.id

      await this.loadMessages(room.id)
      this.joinRoom(room.id)
    },
    async loadMessages(roomId) {
      this.loadingMessages = true
      try {
        const { messages } = await fetchRoomMessages(roomId, 100)
        this.messages = messages
      } catch (error) {
        console.error('Erro ao carregar mensagens', error)
        this.socketError = error.response?.data?.error || 'Não foi possível carregar as mensagens.'
        this.messages = []
      } finally {
        this.loadingMessages = false
      }
    },
    joinRoom(roomId) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        return
      }

      if (this.joinedRooms.has(roomId)) {
        return
      }

      this.socket.send(JSON.stringify({ type: 'join', roomId }))
    },
    sendMessage() {
      if (!this.activeRoom || !this.socketReady || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
        return
      }

      const payload = this.buildMessagePayload()
      if (!payload) {
        this.socketError = 'Inclua texto, uma imagem ou uma rolagem de dado para enviar.'
        return
      }

      this.sendingMessage = true
      try {
        this.socket.send(JSON.stringify({
          type: 'message',
          roomId: this.activeRoom.id,
          content: payload
        }))
        this.messageDraft = ''
        this.pendingDiceRoll = null
        this.attachedImage = null
        this.attachmentError = ''
        this.emojiPaletteVisible = false
      } catch (error) {
        console.error('Erro ao enviar mensagem', error)
        this.socketError = 'Não foi possível enviar a mensagem.'
      } finally {
        this.sendingMessage = false
      }
    },
    scrollMessagesToBottom() {
      const el = this.$refs.messagesContainer
      if (!el) {
        return
      }
      el.scrollTop = el.scrollHeight
    },
    appendEmoji(emoji) {
      this.messageDraft = `${this.messageDraft || ''}${emoji}`
      this.emojiPaletteVisible = false
    },
    triggerImageUpload() {
      this.attachmentError = ''
      this.$refs.imageInput?.click()
    },
    handleImageSelect(event) {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }

      if (file.size > 4000000) {
        this.attachmentError = 'Envie imagens menores que 4MB.'
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result
        if (typeof dataUrl === 'string' && dataUrl.length < 7500) {
          this.attachedImage = { name: file.name, data: dataUrl }
          this.attachmentError = ''
        } else {
          this.attachedImage = null
          this.attachmentError = 'A imagem está muito pesada para o chat.'
        }
      }
      reader.readAsDataURL(file)
    },
    rollDice() {
      if (this.diceCooldownActive) return

      this.diceCooldownActive = true

      this.socket.send(JSON.stringify({
        type: 'dice-roll',
        roomId: this.activeRoom.id
      }))

      setTimeout(() => {
        this.diceCooldownActive = false
      }, 3000)
    },


    buildMessagePayload() {
      const text = (this.messageDraft || '').trim()
      const hasExtras = this.pendingDiceRoll || this.attachedImage || this.microphoneEnabled
      if (!text && !hasExtras) {
        return ''
      }

      const content = {
        text,
        audioEnabled: this.microphoneEnabled,
        diceRoll: this.pendingDiceRoll,
        image: this.attachedImage
      }

      const serialized = JSON.stringify(content)
      if (!serialized || serialized.length > 7900) {
        this.attachmentError = 'A mensagem ficou muito grande para ser enviada. Reduza o conteúdo.'
        return ''
      }

      return serialized
    },
    openPrivateModal() {
      if (this.privateRoomLimitReached) {
        this.privateRoomError = 'Você já atingiu o limite de 3 salas particulares.'
        return
      }

      this.privateRoomForm = {
        name: '',
        participants: [],
        accessType: 'private',
        password: '',
        maxUsers: 5
      }
      this.privateRoomError = ''
      this.privateManagementError = ''
      this.showPrivateModal = true
    },
    closePrivateModal() {
      this.showPrivateModal = false
    },
    async submitPrivateRoom() {
      const trimmedName = this.privateRoomForm.name.trim()
      if (!trimmedName) {
        this.privateRoomError = 'O título da sala é obrigatório.'
        return
      }

      if (!this.privateRoomForm.accessType) {
        this.privateRoomError = 'Escolha se a sala será pública ou privada.'
        return
      }

      if (this.privateRoomForm.accessType === 'private' && !this.privateRoomForm.password.trim()) {
        this.privateRoomError = 'Defina uma senha para salas privadas.'
        return
      }

      if (this.privateRoomLimitReached) {
        this.privateRoomError = 'Você já possui 3 salas particulares.'
        return
      }

      const participants = this.privateRoomForm.participants.map((id) => Number(id)).filter((id) => Number.isInteger(id))
      if (participants.length + 1 > 5) {
        this.privateRoomError = 'O limite é de 5 participantes, contando com você.'
        return
      }

      this.privateRoomError = ''
      this.creatingRoom = true
      try {
        const { room } = await createPrivateRoom({
          name: trimmedName,
          participantIds: participants,
          accessType: this.privateRoomForm.accessType,
          password: this.privateRoomForm.accessType === 'private' ? this.privateRoomForm.password : null,
          maxUsers: 5
        })
        this.rooms.private.push({ ...room, is_member: true, is_owner: true, member_count: participants.length + 1 })
        this.selectRoom(room)
        this.showPrivateModal = false
      } catch (error) {
        this.privateRoomError = error.response?.data?.error || 'Não foi possível criar a sala.'
      } finally {
        this.creatingRoom = false
      }
    },
    async sendChatFriendRequest() {
      if (!this.currentUserId) {
        this.friendRequestError = 'É necessário estar autenticado para enviar pedidos.'
        this.friendRequestMessage = ''
        return
      }

      if (!this.friendRequestIdentifier.trim()) {
        this.friendRequestError = 'Informe email, apelido ou nome completo.'
        this.friendRequestMessage = ''
        return
      }

      this.friendRequestError = ''
      this.friendRequestMessage = ''
      this.friendRequestBusy = true

      try {
        const { data } = await sendFriendRequest(this.currentUserId, this.friendRequestIdentifier.trim())
        this.friendRequestMessage = data.message || 'Pedido enviado com sucesso.'
        this.friendRequestIdentifier = ''
      } catch (error) {
        this.friendRequestError = error.response?.data?.error || 'Não foi possível enviar o pedido.'
      } finally {
        this.friendRequestBusy = false
      }
    },
    async startDirectConversation() {
      if (!this.directTargetId) {
        this.directError = 'Selecione um usuário para iniciar a conversa.'
        return
      }

      this.directError = ''
      try {
        const targetId = Number(this.directTargetId)
        if (!Number.isInteger(targetId)) {
          this.directError = 'Selecione um usuário válido.'
          return
        }

        const { room, targetUser } = await openDirectConversation(targetId)
        const existing = this.findRoom(room.id)
        const formattedRoom = {
          ...room,
          target_user: targetUser || null
        }
        if (!existing) {
          this.rooms.direct.push(formattedRoom)
        }
        this.selectRoom(formattedRoom)
        this.directTargetId = ''
      } catch (error) {
        this.directError = error.response?.data?.error || 'Não foi possível iniciar a conversa.'
      }
    },
    handleRoomSelection(room) {
      if (room.room_type === 'private' && !room.is_member) {
        if (room.access_type === 'public') {
          this.joinPublicRoom(room)
          return
        }
        this.openJoinModal(room)
        return
      }

      this.selectRoom(room)
    },
    async joinPublicRoom(room) {
      if (!room) {
        return
      }

      this.joiningRoom = true
      this.joinError = ''
      try {
        const { room: joined } = await joinCustomRoom(room.id)
        this.updateRoomMembership(joined)
        this.selectRoom({ ...room, ...joined, is_member: true })
      } catch (error) {
        this.joinError = error.response?.data?.error || 'Não foi possível entrar na sala.'
        this.socketError = this.joinError
      } finally {
        this.joiningRoom = false
      }
    },
    openJoinModal(room) {
      this.joinTargetRoom = room
      this.joinPassword = ''
      this.joinError = ''
      this.showJoinModal = true
    },
    closeJoinModal() {
      this.showJoinModal = false
      this.joinTargetRoom = null
      this.joinPassword = ''
      this.joinError = ''
    },
    async submitJoinRoom() {
      if (!this.joinTargetRoom) {
        return
      }

      this.joiningRoom = true
      this.joinError = ''
      try {
        const { room } = await joinCustomRoom(this.joinTargetRoom.id, { password: this.joinPassword })
        this.updateRoomMembership(room)
        this.selectRoom({ ...room, is_member: true })
        this.closeJoinModal()
      } catch (error) {
        this.joinError = error.response?.data?.error || 'Senha inválida para esta sala.'
      } finally {
        this.joiningRoom = false
      }
    },
    updateRoomMembership(joinedRoom) {
      this.rooms.private = this.rooms.private.map((room) => {
        if (room.id === joinedRoom.id) {
          return { ...room, ...joinedRoom, is_member: true }
        }
        return room
      })
    },
    async confirmDeleteRoom(room) {
      if (!room || room.owner_id !== this.currentUserId) {
        return
      }

      this.privateManagementError = ''
      this.roomToDelete = room
      this.showDeleteModal = true
    },

    async deleteRoomConfirmed() {
      if (!this.roomToDelete) return

      const room = this.roomToDelete
      this.deletingRoomId = room.id
      this.privateManagementError = ''

      try {
        await deletePrivateRoom(room.id)

        this.rooms.private = this.rooms.private.filter(
          (item) => item.id !== room.id
        )

        if (this.activeRoom && this.activeRoom.id === room.id) {
          this.activeRoom = null
          this.messages = []
          this.selectFallbackRoom(room.id)
        }

        this.showDeleteModal = false
        this.roomToDelete = null
      } catch (error) {
        this.privateManagementError =
          error.response?.data?.error || 'Não foi possível excluir a sala.'
      } finally {
        this.deletingRoomId = null
      }
    },

    cancelDeleteRoom() {
      this.showDeleteModal = false
      this.roomToDelete = null
    },

    selectFallbackRoom(removedRoomId) {
      const candidates = [
        ...this.rooms.public,
        ...this.rooms.private.filter((room) => room.id !== removedRoomId && room.is_member),
        ...this.rooms.direct
      ]

      const nextRoom = candidates.find(Boolean)
      if (nextRoom) {
        this.handleRoomSelection({ ...nextRoom })
      }
    },
    notifyPremiumRequired() {
      this.socketError = 'Apenas usuários premium podem criar salas particulares.'
    },
    findRoom(roomId) {
      return (
        this.rooms.public.find((room) => room.id === roomId)
        || this.rooms.private.find((room) => room.id === roomId)
        || this.rooms.direct.find((room) => room.id === roomId)
      )
    },
    formatDate(value) {
      if (!value) {
        return ''
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return ''
      }
      return date.toLocaleString()
    },
    normalizeMessageContent(message) {
      if (!message || !message.content) {
        return { text: '' }
      }

      if (typeof message.content === 'string') {
        try {
          const parsed = JSON.parse(message.content)
          return {
            text: parsed.text || '',
            image: parsed.image || null,
            diceRoll: parsed.diceRoll || null,
            audioEnabled: !!parsed.audioEnabled
          }
        } catch (error) {
          return { text: message.content }
        }
      }

      return message.content
    },

    async toggleVoice() {
      if (this.microphoneEnabled) {
        this.disableVoice()
      } else {
        await this.enableVoice()
      }
    },

    async enableVoice() {
      if (!this.activeRoom || !this.socket || !this.socketReady) {
        return
      }

      try {
        this.voiceStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.microphoneEnabled = true
        this.currentVoiceRoomId = this.activeRoom.id

        const track = this.voiceStream.getAudioTracks()[0]
        if (track) {
          track.enabled = !this.pushToTalk || this.pushToTalkActive
        }

        this.applyVoiceQuality(this.voiceQuality)
        this.monitorStreamActivity(this.currentUserId, this.voiceStream)

        // Entra no áudio da sala
        this.socket.send(JSON.stringify({
          type: 'voice-join',
          roomId: this.activeRoom.id
        }))
      } catch (err) {
        console.error('Erro ao acessar microfone:', err)
        this.socketError = 'Não foi possível acessar o microfone.'
      }
    },

    disableVoice() {
      this.microphoneEnabled = false
      const leavingRoomId = this.currentVoiceRoomId || (this.activeRoom && this.activeRoom.id)

      if (this.voiceStream) {
        this.voiceStream.getTracks().forEach(track => track.stop())
        this.voiceStream = null
        this.stopStreamMonitor(this.currentUserId)
      }

      if (leavingRoomId && this.socket) {
        this.socket.send(JSON.stringify({
          type: 'voice-leave',
          roomId: leavingRoomId
        }))
      }

      Object.values(this.voicePeers).forEach(pc => pc.close())
      this.voicePeers = {}

      Object.keys(this.audioElements).forEach((key) => this.removeAudioElement(Number(key)))
      Object.keys(this.audioAnalysers).forEach((key) => this.stopStreamMonitor(Number(key)))
      this.currentVoiceRoomId = null
    },

    createVoicePeer(userId) {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:195.35.18.193:3478' },
          {
            urls: 'turn:195.35.18.193:3478',
            username: 'webrtc',
            credential: 'VmRpg@2025'
          }
        ]
      })

      if (this.voiceStream) {
        this.voiceStream.getTracks().forEach(track => {
          pc.addTrack(track, this.voiceStream)
        })
      }

      pc.onicecandidate = (event) => {
        if (event.candidate && this.socket && this.activeRoom) {
          this.socket.send(JSON.stringify({
            type: 'voice-ice',
            roomId: this.activeRoom.id,
            targetUserId: userId,
            candidate: event.candidate
          }))
        }
      }

      pc.ontrack = (event) => {
        const audio = document.createElement('audio')
        audio.srcObject = event.streams[0]
        audio.autoplay = true
        audio.dataset.userId = String(userId)
        audio.volume = this.voiceVolumes[userId] ?? 1
        audio.muted = !!this.mutedUsers[userId]
        document.body.appendChild(audio)

        this.audioElements[userId] = audio
        this.monitorStreamActivity(userId, event.streams[0])
      }

      this.voicePeers[userId] = pc
      return pc
    },

    monitorStreamActivity(userId, stream) {
      if (!stream) {
        return
      }

      this.stopStreamMonitor(userId)

      try {
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext
        if (!AudioContextCtor) {
          return
        }

        const context = new AudioContextCtor()
        const source = context.createMediaStreamSource(stream)
        const analyser = context.createAnalyser()
        analyser.fftSize = 512
        const bufferLength = analyser.fftSize
        const dataArray = new Uint8Array(bufferLength)
        source.connect(analyser)

        const tick = () => {
          analyser.getByteTimeDomainData(dataArray)
          let sumSquares = 0
          for (let i = 0; i < bufferLength; i += 1) {
            const value = (dataArray[i] - 128) / 128
            sumSquares += value * value
          }
          const volume = Math.sqrt(sumSquares / bufferLength)
          this.updateSpeakingState(userId, volume > 0.03)
          this.audioAnalysers[userId].raf = requestAnimationFrame(tick)
        }

        const raf = requestAnimationFrame(tick)
        this.audioAnalysers[userId] = { context, analyser, raf }
      } catch (error) {
        console.warn('Não foi possível monitorar atividade de voz', error)
      }
    },

    stopStreamMonitor(userId) {
      const monitor = this.audioAnalysers[userId]
      if (monitor) {
        if (monitor.raf) {
          cancelAnimationFrame(monitor.raf)
        }
        monitor.context?.close?.()
        delete this.audioAnalysers[userId]
      }
      this.updateSpeakingState(userId, false)
    },

    updateSpeakingState(userId, speaking) {
      this.speakingUsers = {
        ...this.speakingUsers,
        [userId]: speaking
      }
    },

    removeAudioElement(userId) {
      const audio = this.audioElements[userId]
      if (audio) {
        try {
          audio.pause()
        } catch (error) {
          // ignore
        }
        audio.srcObject = null
        audio.remove()
        delete this.audioElements[userId]
      }
    },

    syncVoicePeersWithRoster(roomId) {
      const allowedIds = new Set((this.voiceRosters[roomId] || []).map((participant) => participant.id))
      Object.entries(this.voicePeers).forEach(([peerId, pc]) => {
        const numericId = Number(peerId)
        if (!allowedIds.has(numericId)) {
          pc.close()
          delete this.voicePeers[peerId]
          this.removeAudioElement(numericId)
          this.stopStreamMonitor(numericId)
        }
      })
    },

    setUserVolume(userId, rawValue) {
      const normalized = Math.max(0, Math.min(Number(rawValue), 100)) / 100
      this.voiceVolumes = {
        ...this.voiceVolumes,
        [userId]: normalized
      }

      const audio = this.audioElements[userId]
      if (audio) {
        audio.volume = normalized
        audio.muted = !!this.mutedUsers[userId] || normalized === 0
      }
    },

    toggleMuteUser(userId) {
      const nextValue = !this.mutedUsers[userId]
      this.mutedUsers = {
        ...this.mutedUsers,
        [userId]: nextValue
      }

      const audio = this.audioElements[userId]
      if (audio) {
        audio.muted = nextValue
      }
    },

    toggleRightPanel() {
      this.rightPanelOpen = !this.rightPanelOpen
    },

    statusDotClass(status) {
      if (status === 'online') return 'dot-online'
      if (status === 'away') return 'dot-away'
      if (status === 'busy') return 'dot-busy'
      return 'dot-off'
    },

    statusLabel(status) {
      if (status === 'online') return 'Online'
      if (status === 'away') return 'Ausente'
      if (status === 'busy') return 'Ocupado'
      return 'Desconhecido'
    },

    isUserSpeaking(userId) {
      return !!this.speakingUsers[userId]
    },

    updateStatus(status) {
      if (!status || this.currentStatus === status) {
        return
      }

      this.currentStatus = status
      if (this.socket && this.socketReady) {
        this.socket.send(JSON.stringify({ type: 'status', status }))
      }

      const nextPresence = {}
      Object.entries(this.presenceByRoom).forEach(([roomId, users]) => {
        nextPresence[roomId] = users.map((user) => (user.id === this.currentUserId
          ? { ...user, status }
          : user))
      })
      this.presenceByRoom = nextPresence
    },

    handlePushToggle(enabled) {
      this.pushToTalk = enabled
      if (this.voiceStream) {
        this.voiceStream.getAudioTracks().forEach((track) => {
          track.enabled = !this.pushToTalk || this.pushToTalkActive
        })
      }
    },

    startTalking() {
      if (!this.microphoneEnabled || !this.voiceStream) {
        return
      }
      this.pushToTalkActive = true
      this.voiceStream.getAudioTracks().forEach((track) => { track.enabled = true })
    },

    stopTalking() {
      if (!this.microphoneEnabled || !this.voiceStream) {
        return
      }
      this.pushToTalkActive = false
      if (this.pushToTalk) {
        this.voiceStream.getAudioTracks().forEach((track) => { track.enabled = false })
      }
    },

    animateDice(message) {
      const content = this.normalizeMessageContent(message)
      if (!content.diceRoll) return

      const sides = content.diceSides || 10
      const finalValue = content.diceRoll

      // Impede reanimar a mesma mensagem
      if (this.animatedDiceValue[message.id] !== undefined) return

      this.animatingDice[message.id] = true
      this.animatedDiceValue[message.id] = '...'

      const interval = setInterval(() => {
        this.animatedDiceValue[message.id] =
          Math.floor(Math.random() * sides) + 1
      }, 80)

      setTimeout(() => {
        clearInterval(interval)
        this.animatedDiceValue[message.id] = finalValue
        this.animatingDice[message.id] = false
      }, 800)
    },

    applyVoiceQuality(level) {
      this.voiceQuality = level
      const bitrateMap = {
        alta: 96000,
        media: 64000,
        baixa: 32000
      }

      const maxBitrate = bitrateMap[level] || null
      Object.values(this.voicePeers).forEach((pc) => {
        pc.getSenders().forEach((sender) => {
          if (sender.track && sender.track.kind === 'audio') {
            const params = sender.getParameters()
            params.encodings = params.encodings || [{}]
            if (maxBitrate) {
              params.encodings[0].maxBitrate = maxBitrate
            } else if (params.encodings[0].maxBitrate) {
              delete params.encodings[0].maxBitrate
            }
            sender.setParameters(params).catch(() => { })
          }
        })
      })
    }

  }
}
</script>

<style scoped>
.chat-page {
  min-height: calc(100vh - 160px);
  padding-bottom: 2rem;
}

.chat-sidebar {
  background: rgba(17, 17, 17, 0.92);
  border: 1px solid rgba(218, 165, 32, 0.15);
  border-radius: 1rem;
  padding: 1.5rem;
  color: #f8f9fa;
  position: sticky;
  top: 90px;
}

.section-title {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: var(--vampire-gold);
}

.room-button {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  border: none;
  background: rgba(255, 255, 255, 0.05);
  color: inherit;
  transition: background 0.2s ease, transform 0.2s ease;
}

.room-button:hover,
.room-button.active {
  background: rgba(139, 0, 0, 0.4);
  transform: translateY(-1px);
}

.status-indicator {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background: #6c757d;
}

.status-indicator.connected {
  background: #28a745;
}

.status-indicator.reconnecting {
  background: #ffc107;
}

.status-indicator.disconnected {
  background: #dc3545;
}

.chat-main {
  background: rgba(17, 17, 17, 0.92);
  border: 1px solid rgba(218, 165, 32, 0.15);
  border-radius: 1rem;
  padding: 1.5rem;
  color: #f8f9fa;
  min-height: 70vh;
  display: flex;
  flex-direction: column;
}

.chat-main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(248, 249, 250, 0.1);
  padding-bottom: 1rem;
  margin-bottom: 1rem;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 0.75rem;
  max-height: 60vh;
}

.chat-message {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.chat-message.mine {
  background: rgba(139, 0, 0, 0.55);
  margin-left: auto;
}

.message-body img {
  max-height: 220px;
  object-fit: contain;
}

.chat-tools .btn.active {
  background: rgba(139, 0, 0, 0.55);
  border-color: var(--vampire-gold);
}

.emoji-palette {
  position: absolute;
  top: 110%;
  left: 0;
  background: rgba(17, 17, 17, 0.95);
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  z-index: 25;
}

.emoji-button {
  background: transparent;
  border: none;
  color: #f8f9fa;
  font-size: 1.1rem;
}

.chat-input {
  margin-top: 1.5rem;
}

.chat-input .form-control {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(218, 165, 32, 0.3);
  color: #f8f9fa;
  resize: none;
}

.chat-input .form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(139, 0, 0, 0.35);
  border-color: var(--vampire-gold);
}

.chat-input .btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.chat-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #adb5bd;
  padding: 2rem 0;
}

.chat-policy-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 2000;
  overflow-y: auto;
}

.chat-policy-card {
  background: rgba(17, 17, 17, 0.95);
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 1rem;
  padding: 2rem;
  max-width: 540px;
  width: 100%;
  color: #f8f9fa;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
}

.chat-policy-card ul {
  padding-left: 1.25rem;
}

.chat-policy-card li {
  margin-bottom: 0.75rem;
}

@media (max-width: 576px) {
  .chat-policy-modal {
    align-items: flex-start;
    padding: 1rem;
  }

  .chat-policy-card {
    padding: 1.25rem;
    max-height: calc(100vh - 1.5rem);
  }
}

.chat-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 2100;
}

.chat-modal {
  background: rgba(17, 17, 17, 0.95);
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 1rem;
  width: 100%;
  max-width: 520px;
}

.chat-modal .modal-header,
.chat-modal .modal-body,
.chat-modal .modal-footer {
  border-color: rgba(248, 249, 250, 0.1);
  color: #f8f9fa;
}

.direct-start .form-select {
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(218, 165, 32, 0.2);
  color: #f8f9fa;
}

.user-panel-toggle {
  position: fixed;
  right: 1.5rem;
  bottom: 2rem;
  z-index: 1030;
  background: var(--vampire-red);
  color: #f8f9fa;
  border: none;
  border-radius: 999px;
  padding: 0.6rem 1.1rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.35);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.user-panel-toggle:hover {
  background: var(--vampire-blood);
}

.user-panel {
  position: fixed;
  inset: 0 0 0 auto;
  width: 360px;
  max-width: 100%;
  background: rgba(17, 17, 17, 0.97);
  color: #f8f9fa;
  border-left: 1px solid rgba(218, 165, 32, 0.25);
  padding: 1.5rem;
  z-index: 1040;
  overflow-y: auto;
}

.user-panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1035;
}

.user-list .user-row,
.voice-list .voice-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid rgba(248, 249, 250, 0.05);
}

.presence-dot {
  width: 0.85rem;
  height: 0.85rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(0, 0, 0, 0.35);
}

.dot-online {
  background: #28a745;
}

.dot-away {
  background: #ffc107;
}

.dot-busy {
  background: #dc3545;
}

.dot-off {
  background: #6c757d;
}

.status-pill {
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.65rem;
  background: rgba(255, 255, 255, 0.1);
}

.status-pill.dot-online {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-pill.dot-away {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-pill.dot-busy {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.voice-actions .form-range {
  width: 120px;
}

.voice-list .voice-row {
  align-items: center;
}

.voice-quality-select {
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(218, 165, 32, 0.25);
  color: #f8f9fa;
}

.speaking-indicator {
  color: var(--vampire-gold);
  display: inline-flex;
  align-items: center;
}

.user-panel-slide-enter-active,
.user-panel-slide-leave-active {
  transition: transform 0.3s ease;
}

.user-panel-slide-enter-from,
.user-panel-slide-leave-to {
  transform: translateX(100%);
}

.room-owner-name {
  color: var(--vampire-gold);
  font-weight: 600;
  text-shadow: 0 0 4px rgba(212, 175, 55, 0.4);
}

.room-owner-name::after {
  content: ' ★';
  font-size: 0.9em;
  opacity: 0.8;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.modal-dialog {
  width: min(420px, 92%);
}

.modal-content {
  background: linear-gradient(145deg, var(--vampire-dark), var(--vampire-gray));
  border: 1px solid var(--vampire-red);
  border-radius: 14px;
  color: #fff;
  box-shadow: 0 20px 40px rgba(0,0,0,.6);
}


@media (max-width: 991.98px) {
  .chat-sidebar {
    position: static;
  }

  .chat-messages {
    max-height: none;
  }
}

.dice-roll-container {
  margin-top: 4px;
}

.dice-roll-number {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 10px;
  background: rgba(139, 0, 0, 0.15);
  color: #ffcc00;
  font-weight: bold;
  font-size: 0.95rem;
  letter-spacing: 0.05em;
  box-shadow: inset 0 0 6px rgba(255, 204, 0, 0.3);
  transition: transform 0.2s ease, filter 0.2s ease;
}

.dice-roll-animate {
  animation: dice-bounce 0.4s ease-in-out infinite alternate;
  filter: brightness(1.4);
}

@keyframes dice-bounce {
  from {
    transform: scale(0.9) rotate(-2deg);
  }

  to {
    transform: scale(1.05) rotate(2deg);
  }
}
</style>
